import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { memo, useMemo } from "react";

import { cdnCommonPath } from "@/constants/cdnUrls";
import { PATHS } from "@/constants/pathnames";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useHeaderContext } from "@/contexts/HeaderContext";
import { Link } from "@/i18n/navigation";
import clsx from "clsx";
import { useLocale } from "next-intl";
import Image from "next/image";
import UserMenuContent from "./UserMenuContent";
import { Button } from "./ui/Button";

const MenuIcon = memo(function MenuIcon({ icon }) {
  return <FontAwesomeIcon icon={icon} />;
});

const LanguageCurrencyMobileButton = memo(
  function LanguageCurrencyMobileButton() {
    const { currency, showLanguageAndCurrencyModal } = useCurrency();
    const locale = useLocale();
    const t = useTranslationDetector();

    return (
      <div className="md:hidden">
        <button
          onClick={showLanguageAndCurrencyModal}
          className="border-primary-weak inline-flex w-full cursor-pointer items-center justify-between gap-2 rounded-xl border p-3"
        >
          <span className="font-medium">{t("common.languageandcurrency")}</span>
          <span className="flex items-center gap-2">
            <Image
              src={cdnCommonPath + `/${locale}.svg`}
              alt={locale}
              width={24}
              height={24}
            />

            <div className="bg-primary-weak w-[1px] self-stretch"></div>
            <span>{currency}</span>
          </span>
        </button>
      </div>
    );
  },
);

const NavbarMenuContent = memo(function NavbarMenuContent({ isCollapsed }) {
  const t = useTranslationDetector();
  const { isLogin, showLoginModal } = useAuth();
  const { showReservationControl } = useHeaderContext();
  const menuData = useMemo(
    () => [
      {
        id: "transportation",
        items: [
          {
            id: "flight",
            text: t("navbar.flights"),
            icon: byPrefixAndName.fak["new-flight"],
            href: PATHS.FLIGHT_TICKET.PATH,
          },
          {
            id: "hotel",
            text: t("navbar.hotel"),
            icon: byPrefixAndName.fak["new-hotel"],
            href: PATHS.HOTEL.PATH,
          },
          {
            id: "bus",
            text: t("navbar.bus"),
            icon: byPrefixAndName.fak["new-bus"],
            href: PATHS.BUS_TICKET.PATH,
          },
          {
            id: "car",
            text: t("navbar.car"),
            icon: byPrefixAndName.fak["new-car"],
            href: PATHS.CAR_RENTAL.PATH,
          },
          {
            id: "ferry",
            text: t("navbar.ferry"),
            icon: byPrefixAndName.fak["new-ferry"],
            href: PATHS.FERRY_TICKET.PATH,
          },
        ],
      },
      {
        id: "services",
        items: [
          {
            id: "creditcard",
            text: t("navbar.cardpoints"),
            icon: byPrefixAndName.fak["new-card"],
            href: PATHS.POINT_CONVERT.PATH,
          },
        ],
      },
      {
        id: "other",
        items: [
          {
            id: "campaigns",
            text: t("navbar.campaigns"),
            icon: byPrefixAndName.fak["new-coupon"],
            href: PATHS.CAMPAIGNS.PATH,
          },
          {
            id: "mobile",
            text: t("navbar.mobileapp"),
            icon: byPrefixAndName.fak["new-phone"],
            href: "https://turna.go.link?adj_t=1yxhgt87&adj_engagement_type=fallback_click",
            target: "_blank",
          },
        ],
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="md:hidden">
        {isLogin ? (
          <UserMenuContent />
        ) : (
          <Button
            onClick={showLoginModal}
            color="primary"
            variant="outline"
            className="w-full"
          >
            {t("common.login")}
          </Button>
        )}
      </div>
      <button
        className="border-primary-weak flex items-center gap-2 rounded-lg border p-3 font-medium md:hidden"
        onClick={showReservationControl}
      >
        <FontAwesomeIcon icon={byPrefixAndName.fal["ticket"]} />
        <span>{t("navbar.reservationcontrol")}</span>
      </button>

      <LanguageCurrencyMobileButton />

      <div className="flex flex-col gap-4">
        {menuData.map((menuCategory, index) => (
          <React.Fragment key={menuCategory.id}>
            <div className="flex flex-col gap-2">
              {menuCategory.items.map((item) => (
                <Link
                  key={item.id}
                  href={item.href}
                  prefetch={true}
                  target={item.target || "_self"}
                  className={clsx(
                    "hover:bg-primary-weaker flex items-center gap-4 rounded-lg p-3 px-2 transition-colors",
                  )}
                >
                  <span>
                    <FontAwesomeIcon
                      icon={item.icon}
                      className="text-[28px] md:text-2xl"
                    />
                  </span>
                  <span
                    className={clsx(
                      "max-w-[200px] truncate font-medium transition-all",
                      isCollapsed && "opacity-0 group-hover:opacity-100",
                    )}
                  >
                    {item.text}
                  </span>
                </Link>
              ))}
            </div>
            {index < menuData.length - 1 && (
              <div className="border-primary-weak border-b"></div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
});

export default NavbarMenuContent;
